import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  getDefaultDesktopPrinter,
  getThermalPaperWidthMm,
  getValidatedPrinterName,
  isDesktopApp,
  isPrinterValidated,
  listDesktopPrinters,
  saveThermalPaperWidthMm,
  saveValidatedPrinterName,
  suggestThermalPaperWidthForPrinter,
  buildPrinterTestHtml,
  silentPrintRestaurantHtml,
} from '../../utils/desktopPrinter';

function PrinterValidationDialog({ open, onClose, onValidated }) {
  const [loading, setLoading] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [paperWidthMm, setPaperWidthMm] = useState(80);
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(false);

  const loadPrinters = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await listDesktopPrinters();
      setPrinters(rows);
      const saved = getValidatedPrinterName();
      const fallback = saved || rows.find((row) => row.isDefault)?.name || rows[0]?.name || '';
      if (!selectedPrinter && fallback) {
        setSelectedPrinter(fallback);
      } else if (fallback && !rows.some((row) => row.name === selectedPrinter)) {
        setSelectedPrinter(fallback);
      }
      if (!rows.length) {
        const defaultPrinter = await getDefaultDesktopPrinter();
        if (defaultPrinter?.name) {
          setPrinters([defaultPrinter]);
          setSelectedPrinter(defaultPrinter.name);
        }
      }
    } catch (err) {
      setError(err?.message || 'Unable to load printers from the desktop app.');
    } finally {
      setLoading(false);
    }
  }, [selectedPrinter]);

  useEffect(() => {
    if (!open) return;
    setPaperWidthMm(getThermalPaperWidthMm());
    void loadPrinters();
  }, [open, loadPrinters]);

  const handleTestPrint = async () => {
    if (!selectedPrinter) {
      setError('Select a printer first.');
      return;
    }
    setTesting(true);
    setError('');
    try {
      const result = await silentPrintRestaurantHtml(
        buildPrinterTestHtml(paperWidthMm, selectedPrinter),
        {
          deviceName: selectedPrinter,
          printKind: 'test',
          paperWidthMm,
        },
      );
      if (!result?.success) {
        setError(result?.error || 'Test print failed.');
      }
    } catch (err) {
      setError(err?.message || 'Test print failed.');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    if (!selectedPrinter) {
      setError('Select a printer to continue.');
      return;
    }
    saveValidatedPrinterName(selectedPrinter);
    saveThermalPaperWidthMm(paperWidthMm);
    onValidated?.(selectedPrinter);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800 }}>Validate restaurant printer</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose the thermal printer and paper width for invoice and KOT prints on this device.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="restaurant-printer-select-label">Printer</InputLabel>
              <Select
                labelId="restaurant-printer-select-label"
                label="Printer"
                value={selectedPrinter}
                onChange={(event) => {
                  const printerName = event.target.value;
                  setSelectedPrinter(printerName);
                  setPaperWidthMm(suggestThermalPaperWidthForPrinter(printerName));
                  setError('');
                }}
              >
                {printers.map((printer) => (
                  <MenuItem key={printer.name} value={printer.name}>
                    {printer.displayName || printer.name}
                    {printer.isDefault ? ' (Default)' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="restaurant-paper-width-label">Paper width</InputLabel>
              <Select
                labelId="restaurant-paper-width-label"
                label="Paper width"
                value={paperWidthMm}
                onChange={(event) => {
                  setPaperWidthMm(Number(event.target.value));
                  setError('');
                }}
              >
                <MenuItem value={80}>80 mm (3 inch) — hotel / restaurant</MenuItem>
                <MenuItem value={58}>58 mm (2 inch)</MenuItem>
              </Select>
            </FormControl>

            {!printers.length ? (
              <Alert severity="info">
                No printers were detected. Connect a printer to this computer, then reopen this dialog.
              </Alert>
            ) : null}

            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="outlined"
          startIcon={<PrintOutlinedIcon />}
          disabled={!selectedPrinter || testing || loading}
          onClick={() => void handleTestPrint()}
        >
          {testing ? 'Printing…' : 'Test print'}
        </Button>
        <Button variant="contained" disabled={!selectedPrinter || loading} onClick={handleSave}>
          Save printer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function PrinterValidationBanner({ onValidated }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [validated, setValidated] = useState(() => isPrinterValidated());

  useEffect(() => {
    const sync = () => setValidated(isPrinterValidated());
    sync();
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  useEffect(() => {
    const openDialog = () => setDialogOpen(true);
    window.addEventListener('roomnexa:open-printer-validation', openDialog);
    return () => window.removeEventListener('roomnexa:open-printer-validation', openDialog);
  }, []);

  if (!isDesktopApp() || validated) {
    return null;
  }

  const openDialog = () => setDialogOpen(true);

  return (
    <>
      <Alert
        severity="warning"
        variant="filled"
        icon={<PrintOutlinedIcon fontSize="inherit" />}
        action={(
          <Button
            color="inherit"
            size="small"
            endIcon={<ChevronRightIcon />}
            onClick={openDialog}
            sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}
          >
            Validate
          </Button>
        )}
        onClick={openDialog}
        sx={{
          mb: 1.5,
          cursor: 'pointer',
          alignItems: 'center',
          borderRadius: 2,
          boxShadow: (theme) => (
            theme.palette.mode === 'dark'
              ? '0 8px 24px rgba(0,0,0,0.35)'
              : '0 8px 20px rgba(15,23,42,0.12)'
          ),
          '& .MuiAlert-message': {
            fontWeight: 700,
            py: 0.25,
          },
          '& .MuiAlert-action': {
            alignItems: 'center',
            pt: 0,
          },
        }}
      >
        Please validate the printer from app
      </Alert>

      <PrinterValidationDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onValidated={(printerName) => {
          setValidated(true);
          onValidated?.(printerName);
        }}
      />
    </>
  );
}
